<template>
  <b-modal
    :id="modalId"
    centered
    title="Upload Presentation Document"
    @hide="$emit('close')"
    hide-header-close
    ok-title="Upload"
    ok-only
    @hidden="onHidden"
    :ok-disabled="this.form.presentation == null"
    @ok="handleOk"
    size="md"
  >
    <div class="p-4">
      <!-- Course Info -->
      <div class="course-info-section mb-4 p-3 rounded" style="background-color: #fafafa">
        <div class="d-flex align-items-center">
          <div class="course-icon me-3">
            <i class="fe fe-book-open text-danger" style="font-size: 1.5rem"></i>
          </div>
          <div>
            <h6 class="mb-1 font-weight-bold" style="font-size: 12px">{{ data?.coaching?.title || "-" }}</h6>
            <small class="text-muted">
              <i class="fe fe-briefcase me-1"></i>
              {{ data?.coaching?.methodology || "-" }}
            </small>
          </div>
        </div>
      </div>

      <!-- Upload Section -->
      <div class="upload-section mb-4">
        <label class="form-label font-weight-bold"> Presentation File <span class="text-danger">*</span> </label>
        <p class="text-muted small mb-2">
          Upload your presentation in image or PDF format (JPG, JPEG, PNG, PDF). Max file size: 2MB
        </p>

        <!-- Dropzone -->
        <div
          class="dropzone p-4 border rounded text-center"
          :class="{ 'border-danger': isDragging, 'is-invalid': !selectedFile && submitted }"
          @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
          @drop.prevent="handleDrop"
        >
          <!-- File Belum Dipilih -->
          <div v-if="!selectedFile && !previewUrl">
            <i class="fe fe-upload-cloud text-muted mb-2" style="font-size: 3rem"></i>
            <p class="text-muted mb-2">Drag & drop your presentation here or</p>
            <label class="btn btn-outline-danger btn-sm">
              Browse Files
              <input type="file" accept="image/jpeg,image/jpg,image/png,application/pdf" @change="handleFileSelect" hidden />
            </label>
          </div>

          <!-- File Selected / Preview -->
          <div v-else>
            <!-- Image Preview -->
            <img
              v-if="previewUrl && !isPdfFile"
              :src="previewUrl"
              alt="Certificate Preview"
              class="img-fluid rounded mb-3"
              style="max-height: 250px"
            />
            <!-- PDF Preview -->
            <div v-else-if="isPdfFile" class="pdf-preview mb-3">
              <div class="pdf-icon-container">
                <i class="fe fe-file-text"></i>
              </div>
              <p class="pdf-label mb-0">PDF File</p>
            </div>
            <div class="d-flex align-items-center justify-content-center gap-2">
              <span class="text-success">
                <i class="fe fe-check-circle me-1"></i>
                {{ selectedFile?.name }}
              </span>
              <button class="btn btn-outline-danger btn-sm" @click="removeFile">
                <i class="fe fe-x"></i>
              </button>
            </div>
            <p class="text-muted small mt-2 mb-0">Size: {{ formatFileSize(selectedFile?.size) }}</p>
          </div>
        </div>
        <small v-if="!selectedFile && submitted" class="text-danger"> Please select a presentation file </small>
      </div>
    </div>
  </b-modal>
</template>

<script>
export default {
  name: "UploadPresentationModal",

  props: {
    modalId: { type: String, default: "upload-presentation-modal" },
    data: { type: Object, default: null },
    isLoading: { type: Boolean, default: false },
  },

  data() {
    return {
      form: {
        presentation: null,
        id: null,
      },
      selectedFile: null,
      previewUrl: null,
      isPdfFile: false,
      note: "",
      isDragging: false,
      isUploading: false,
      submitted: false,
    };
  },

  methods: {
    onHidden() {
      this.resetForm();
    },

    handleOk(bvModalEvt) {
      bvModalEvt.preventDefault();
      this.handleSubmit();
    },

    handleSubmit() {
      const payload = { ...this.form };

      this.$emit("upload-presentation", payload);
    },

    handleChangeFile(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.form.presentation = file;
      this.form.id = this.data.id;
    },

    resetForm() {
      this.form = { presentation: null, id: null };
    },

    handleFileSelect(event) {
      const file = event.target.files[0];
      this.processFile(file);
    },

    handleDrop(event) {
      this.isDragging = false;
      const file = event.dataTransfer.files[0];
      this.processFile(file);
    },

    processFile(file) {
      if (!file) return;

      const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
      if (!allowedTypes.includes(file.type)) {
        this.$swal({
          title: "Tipe File Tidak Valid",
          text: "Unggah file gambar (JPG, JPEG, PNG) atau PDF",
          icon: "error",
          confirmButtonText: "OK",
        });
        return;
      }

      const maxSize = 2 * 1024 * 1024;
      if (file.size > maxSize) {
        this.$swal({
          title: "File Terlalu Besar",
          text: "Ukuran file maksimal 2MB",
          icon: "error",
          confirmButtonText: "OK",
        });
        return;
      }

      this.selectedFile = file;
      this.isPdfFile = file.type === "application/pdf";

      this.form.presentation = file;
      this.form.id = this.data.id;

      if (!this.isPdfFile) {
        this.previewUrl = URL.createObjectURL(file);
      } else {
        this.previewUrl = null;
      }
    },

    removeFile() {
      this.selectedFile = null;
      this.isPdfFile = false;
      this.form.presentation = null;

      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
        this.previewUrl = null;
      }
    },

    formatFileSize(bytes) {
      if (!bytes) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    },
  },

  beforeDestroy() {
    // Clean up object URL
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
    }
  },
};
</script>

<style scoped>
.dropzone {
  background-color: #f8f9fa;
  border: 2px dashed #dee2e6 !important;
  cursor: pointer;
  transition: all 0.3s ease;
}

.dropzone:hover,
.dropzone.border-danger {
  border-color: #e63757 !important;
  background-color: #fff5f7;
}

.dropzone.is-invalid {
  border-color: #dc3545 !important;
}

.course-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: #fce4e4;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gap-2 {
  gap: 0.5rem;
}

.me-1 {
  margin-right: 0.25rem;
}

.me-2 {
  margin-right: 0.5rem;
}

.me-3 {
  margin-right: 1rem;
}

/* PDF Preview Styles */
.pdf-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.pdf-preview .pdf-icon-container {
  width: 80px;
  height: 100px;
  background: linear-gradient(135deg, #e63757 0%, #c82333 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(230, 55, 87, 0.3);
}

.pdf-preview .pdf-icon-container i {
  font-size: 2.5rem;
  color: #fff;
}

.pdf-preview .pdf-label {
  margin-top: 1rem;
  font-weight: 600;
  color: #1a202c;
}
</style>
